import IconButton from "@/common/components/IconButton";
import { eventNames, shopWindowTypes, valueNames } from "@/modules/Constants";
import RadioButtonBar, {
  orientationType,
} from "@/components/shared/RadioButtonBar";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import type { ExperienceState } from "@/types/state";

const REGION = "Shop Window Radio Button Bar";

interface ConfigList {
  count: number;
  atIndex(index: number): ShopWindowConfig;
}

interface BarConfig extends ShopWindowConfig {
  icon: { value: string; name: string };
}

type ShopWindowRadioButtonBarProps = {
  experienceState: ExperienceState;
  onApplicationEvent?: (event: Record<string, unknown>) => void;
};

// Recipe tabs only. Pixie is reached through the Recipe | Pixie switch, not
// this bar. When Ask is the open window the bar index stays -1 and no cursor shows.
function barConfigs(experienceState: ExperienceState): BarConfig[] {
  const configs = experienceState.applicationData.getValue(
    valueNames.SHOP_WINDOW_CONFIG_LIST,
  ) as ConfigList | undefined;
  const rest: BarConfig[] = [];
  if (configs) {
    for (let n = 0, max = configs.count; n < max; n++) {
      const config = configs.atIndex(n) as BarConfig;
      if (config.type !== shopWindowTypes.ASK) {
        rest.push(config);
      }
    }
  }
  return rest;
}

export default function ShopWindowRadioButtonBar({
  experienceState,
  onApplicationEvent,
}: ShopWindowRadioButtonBarProps) {
  const onSetValue = (_key: string | undefined, value: number) => {
    // `value` is the clicked button's index within the filtered bar.
    const config = barConfigs(experienceState)[value];
    if (config && onApplicationEvent) {
      onApplicationEvent({
        region: REGION,
        name: eventNames.SET_SHOP_WINDOW,
        shopWindowConfig: config,
      });
    }
  };

  const applicationData = experienceState.applicationData;
  const orientation = applicationData.isPortrait()
    ? orientationType.LANDSCAPE
    : orientationType.PORTRAIT;
  const configs = barConfigs(experienceState);
  if (!configs.length) {
    return null;
  }
  const shopWindow = applicationData.getValue(valueNames.SHOP_WINDOW_CONFIG) as
    | ShopWindowConfig
    | null
    | undefined;

  const buttons = [];
  // -1 = nothing selected in the bar, so RadioButtonBar hides its cursor.
  let index = -1;
  for (let n = 0; n < configs.length; n++) {
    const config = configs[n];
    buttons.push(
      <IconButton
        key={`toggle-btn-${n}`}
        className={`${config.name}-radio-btn`}
        dataName={config.name}
        title={config.title}
        icon={config.icon.value}
        iconName={config.icon.name}
      />,
    );
    if (shopWindow && shopWindow.name === config.name) {
      index = n;
    }
  }

  return (
    <RadioButtonBar
      index={index}
      className="tw:w-full tw:[.portrait_&]:mr-[5px] tw:[.portrait_&]:h-full tw:[.portrait_&]:w-auto"
      onSetValue={onSetValue}
      orientation={orientation}
    >
      {buttons}
    </RadioButtonBar>
  );
}
